/* eslint-disable react/jsx-key */
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ROUTES } from '~/constants/routes';
import { BUTTON, FONT_SIZES, PALETTE } from '~/assets/styles/base';
import GmoAskLogo from '~/assets/images/gmoask-logo.png';
import GoogleFormsLogo from '~/assets/images/googleforms-logo.png';

const TitleWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  text-align: left;
  font-size: ${FONT_SIZES.title1};
  margin-top: 84px;
`;

const Title = styled(Typography)``;

const Category = styled(Typography)`
  font-size: ${FONT_SIZES.title2};
  margin-right: 20px;
`;

const TitleDescription = styled(Typography)`
  font-size: ${FONT_SIZES.title2};
  margin-right: 20px;
  display: block;
`;

const SelectionBox = styled.div`
  padding-top: 32px;
  display: flex;
  gap: 40px;
`;

const RegisterBox = styled.div`
  display: flex;
  width: 460px;
  height: 357px;
  border-radius: ${BUTTON.roundedBorder.radius};
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.table};
  box-shadow: 2px 2px 4px #959fa846;
  cursor: pointer;
  transition: opacity 0.4s;
  &:hover {
    opacity: 0.6;
  }
`;

type ImgProps = {
  $type: string;
};
const Img = styled.img<ImgProps>`
  margin-top: ${(props) => (props.$type === 'gmoask' ? '57px' : '44px')};
  margin-left: ${(props) => (props.$type === 'googleforms' ? '57px' : '65px')};
  margin-right: 20px;
`;

const BoxTitle = styled.div`
  font-size: ${FONT_SIZES.tableBody1};
  margin-top: 57px;
`;

const BoxTitleContent = styled.p`
  font-size: ${FONT_SIZES.title4};
  padding-bottom: 4px;
`;

const BoxGoogleTitle = styled.div`
  font-size: ${FONT_SIZES.tableBody1};
  margin-top: 67px;
`;

type RightIconProps = {
  $marginLeft: string;
};
const RightIcon = styled(ChevronRightIcon)<RightIconProps>`
  margin-top: 60px;
  margin-left: ${(props) => props.$marginLeft};
  color: #a3b1be;
  font-size: 42px;
`;

const FeatureWrapper = styled.div`
  position: absolute;
  margin-left: 65px;
  margin-top: 133px;
  display: flex;
  flex-direction: column;
`;

const FeatureDescription = styled.div`
  display: flex;
  flex-direction: row;
`;

const FeatureText = styled(Typography)`
  font-size: ${FONT_SIZES.title3};
  margin-top: 20px;
`;

const SurveyRegisterSelection: React.FC<Record<string, never>> = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const originalFormDesc = t('registration.type.originalForm.description', { returnObjects: true });
  const gFormsDesc = t('registration.type.googleforms.description', { returnObjects: true });

  return (
    <>
      <TitleWrapper data-testid="uiParts-PageTitle">
        <Category>{t('pageTitles.user.surveyRegisterSelection.category')}</Category>
        <Title>{t('pageTitles.user.surveyRegisterSelection.title')}</Title>
      </TitleWrapper>
      <TitleDescription>{t('pageTitles.user.surveyRegisterSelection.desc')}</TitleDescription>

      <SelectionBox>
        {/* Original Form selection type */}
        <RegisterBox
          data-testid="uiParts-SelectionBox-originalForm"
          onClick={() => navigate(ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM)}
        >
          <Img
            $type="gmoask"
            src={GmoAskLogo}
            width="71px"
            height="35px"
            alt="GMO Ask"
            title="GMO Ask"
          />
          <BoxTitle>
            <BoxTitleContent>
              {t('registration.type.originalForm.titleOriginalForm')}
            </BoxTitleContent>
            {t('registration.type.originalForm.title')}
          </BoxTitle>
          <RightIcon $marginLeft="110px" />
          <FeatureWrapper>
            {Array.isArray(originalFormDesc) &&
              originalFormDesc.map((description, descIdx) => (
                <FeatureDescription key={`originalFormDesc-${descIdx}`}>
                  <FeatureText>{description}</FeatureText>
                </FeatureDescription>
              ))}
          </FeatureWrapper>
        </RegisterBox>

        {/* Google Forms selection type */}
        <RegisterBox
          data-testid="uiParts-SelectionBox-googleForms"
          onClick={() => navigate(ROUTES.USER.SURVEY_REGISTER_GOOGLE_FORMS)}
        >
          <Img
            $type="googleforms"
            src={GoogleFormsLogo}
            width="64px"
            height="62px"
            alt="Google Forms"
            title="Google Forms"
          />
          <BoxGoogleTitle>{t('registration.type.googleforms.title')}</BoxGoogleTitle>
          <RightIcon $marginLeft="45px" />
          <FeatureWrapper>
            {Array.isArray(gFormsDesc) &&
              gFormsDesc.map((description, descIdx) => (
                <FeatureDescription key={`gFormsDesc-${descIdx}`}>
                  <FeatureText>{description}</FeatureText>
                </FeatureDescription>
              ))}
          </FeatureWrapper>
        </RegisterBox>
      </SelectionBox>
    </>
  );
};

export default SurveyRegisterSelection;
